import React, { forwardRef, useMemo, useState } from "react";
import { calcular, fmt } from "../../utils/presupuestoCalc";
import PrecioBlock from "./PrecioBlock";
import logoHabitia from "../../assets/logo.png";
import isotipoHabitia from "../../assets/isotipo.png";
import "./presupuestoTemplate.css";

// Documento imprimible del presupuesto comercial de Habitia.
// Port estructural de las 6 paginas del HTML standalone, con styling fiel al original.
// Recibe un `config` (PresupuestoConfig) y un `ref` opcional al elemento raiz para captura por html2canvas.

const PresupuestoTemplate = forwardRef(function PresupuestoTemplate({ config }, ref) {
  const c = config;
  const { total, totalUnidades, totalEmpleados, promoTotal } = useMemo(
    () => calcular(c),
    [c]
  );

  // Estado de carga de las screenshots de plataforma (pagina 5).
  // Si el archivo no existe en /public, mostramos un fallback estilizado en vez del icono roto.
  const [desktopError, setDesktopError] = useState(false);
  const [mobileError, setMobileError] = useState(false);

  return (
    <div className="habitia-presupuesto" ref={ref}>
      {/* ============== PAGINA 1 — COVER ============== */}
      <section className="page cover">
        <div className="cover-top">
          <img src={logoHabitia} alt="habitia" />
          <div className="meta">
            <b>Propuesta&nbsp;Nº&nbsp;{c.propuestaNro}</b>
            <span>{c.ciudad || "Montevideo"} · {c.fecha}</span>
          </div>
        </div>

        <div className="h-stage" aria-hidden="true">
          <div className="h-mark">
            <div className="bar-l" />
            <div className="bar-c" />
            <div className="bar-r" />
          </div>
        </div>

        <div className="cover-copy">
          <span className="eyb">Edificios que evolucionan</span>
          <h1>
            La forma moderna<br />
            de administrar<br />
            <em>edificios.</em>
          </h1>
          <p className="lead">
            {c.coverLead || `Propuesta de gestión integral preparada para ${c.edificio}. Tecnología, transparencia y un equipo dedicado a tu comunidad.`}
          </p>
        </div>

        <div className="cover-bottom">
          <div><div className="lbl">Cliente</div><div className="val">{c.clienteNombre || c.edificio}</div></div>
          <div><div className="lbl">Domicilio</div><div className="val">{c.ubicacion}</div></div>
          <div><div className="lbl">Validez</div><div className="val">{c.validez}</div></div>
          <div><div className="lbl">Contacto</div><div className="val">{c.contacto}</div></div>
        </div>
      </section>

      {/* ============== PAGINA 2 — QUIENES SOMOS ============== */}
      <section className="page">
        <div className="page-pad">
          <span className="sec-tag">01 · Sobre habitia</span>
          <h2 className="big">
            Liderando la nueva generación de <em>administradores de edificios</em> en Uruguay.
          </h2>
          <p className="lead">
            Somos un equipo joven que combina gestión profesional con tecnología propia. Construimos relaciones de largo plazo con cada copropiedad, escuchando a la Comisión y respondiendo rápido a los vecinos.
          </p>

          <div className="quad" style={{ marginTop: 24 }}>
            <div className="card dark">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="8" r="3.5" />
                  <path d="M2 21c0-3.5 3-6 7-6s7 2.5 7 6" />
                  <circle cx="17" cy="9" r="2.5" />
                  <path d="M22 19c0-2-1.5-4-4-4" />
                </svg>
              </div>
              <h3>Equipo dedicado</h3>
              <p>Un administrador asignado al edificio, respaldado por especialistas en finanzas, legales y tecnología.</p>
            </div>
            <div className="card">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="6" y="2" width="12" height="20" rx="2" />
                  <path d="M11 18h2" />
                </svg>
              </div>
              <h3>Tecnología propia</h3>
              <p>App nativa para propietarios e inquilinos, asistente IA y dashboard del administrador en tiempo real.</p>
            </div>
            <div className="card">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="6" width="18" height="13" rx="2" />
                  <path d="M3 10h18" />
                  <path d="M7 15h4" />
                </svg>
              </div>
              <h3>Transparencia financiera</h3>
              <p>Cuenta bancaria del edificio en vivo, conciliación automática y comprobantes online por unidad.</p>
            </div>
            <div className="card">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12l2 2 4-4 4 4 5-5 3 3" />
                  <path d="M3 21h18" />
                </svg>
              </div>
              <h3>Optimización con IA</h3>
              <p>Análisis automático de gastos, alertas de desvíos y sugerencias para reducir costos operativos.</p>
            </div>
          </div>

          {/* Radiografía de catastro (solo propuesta personalizada) */}
          {Array.isArray(c.radiografia) && c.radiografia.length > 0 && (
            <>
              <div className="radiografia">
                {c.radiografia.map((rc, i) => (
                  <div className="rc" key={i}><div className="n">{rc.n}</div><div className="l">{rc.l}</div></div>
                ))}
              </div>
              {c.notaRadiografia && <p className="tiny muted" style={{ marginTop: 8 }}>{c.notaRadiografia}</p>}
            </>
          )}
        </div>
        <div className="pfooter">
          <div className="br"><img src={isotipoHabitia} alt="" />habitia · Propuesta de Administración</div>
          <div className="pn">02 / 06</div>
        </div>
      </section>

      {/* ============== PAGINA 3 — SERVICIOS / PERSONALIZADO ============== */}
      {Array.isArray(c.personalizado) && c.personalizado.length > 0 ? (
        <section className="page">
          <div className="page-pad">
            <span className="sec-tag">02 · Cómo te resuelve habitia</span>
            <h2 className="big">Cada funcionalidad, <em>sobre tu edificio.</em></h2>
            <p className="lead">
              Lo mismo que hacemos en todos lados, puesto a trabajar en lo concreto de {c.edificio}.
            </p>
            <div className="chev-list">
              {c.personalizado.map((s, i) => (
                <div className="chev-row" key={i}>
                  <div className="head tall">{s.titulo}</div>
                  <div className="body"><p className="perso">{s.cuerpo}</p></div>
                </div>
              ))}
            </div>
          </div>
          <div className="pfooter">
            <div className="br"><img src={isotipoHabitia} alt="" />habitia · Propuesta de Administración</div>
            <div className="pn">03 / 06</div>
          </div>
        </section>
      ) : (
      <section className="page">
        <div className="page-pad">
          <span className="sec-tag">02 · Servicios</span>
          <h2 className="big">
            Una sola propuesta. <em>Todo lo que necesita el edificio.</em>
          </h2>
          <p className="lead">
            El servicio mensual cubre la administración completa del edificio bajo el marco de la Ley Nº 10.751 de Propiedad Horizontal.
          </p>

          <div className="chev-list">
            <div className="chev-row">
              <div className="head">Administración general</div>
              <div className="body">
                <span>Custodia documental digital</span>
                <span>Comunicación con Comisión</span>
                <span>Circulares y novedades</span>
                <span>Solicitud de presupuestos</span>
                <span>Tramitación de siniestros</span>
              </div>
            </div>
            <div className="chev-row">
              <div className="head">Gestión financiera</div>
              <div className="body">
                <span>Cobro de gastos comunes</span>
                <span>Estado de cuenta por unidad</span>
                <span>Tesorería y conciliación</span>
                <span>Gestión de deudores</span>
                <span>Presupuesto anual</span>
              </div>
            </div>
            <div className="chev-row">
              <div className="head">Asuntos legales y laborales</div>
              <div className="body">
                <span>Liquidación de sueldos</span>
                <span>Aportes BPS / FONASA / DGI</span>
                <span>Asambleas de copropietarios</span>
                <span>Redacción de actas</span>
                <span>Cumplimiento Ley 10.751</span>
              </div>
            </div>
            <div className="chev-row">
              <div className="head">Plataforma habitia</div>
              <div className="body">
                <span>App iOS y Android</span>
                <span>Asistente IA 24/7</span>
                <span>Reporte de incidencias en vivo</span>
                <span>Acceso a Pagos y Gastos</span>
                <span>Estado de cuenta y comprobantes</span>
                <span>Reglamento, actas y novedades</span>
                <span>Notificaciones push en tiempo real</span>
                <span>Acceso a proveedores del edificio</span>
              </div>
            </div>
          </div>
        </div>
        <div className="pfooter">
          <div className="br"><img src={isotipoHabitia} alt="" />habitia · Propuesta de Administración</div>
          <div className="pn">03 / 06</div>
        </div>
      </section>
      )}

      {/* ============== PAGINA 4 — PROPUESTA ECONOMICA ============== */}
      <section className="page page-precio">
        <div className="page-pad">
          <span className="sec-tag">03 · Propuesta económica</span>
          <h2 className="big">Honorarios mensuales para tu edificio.</h2>
          <p className="lead">
            {c.precioLead || (
              <>
                Esta propuesta contempla la administración integral de {c.edificio}: {totalUnidades} unidades
                {c.amenities.length > 0 ? `, ${c.amenities.length} amenities` : ""}
                {totalEmpleados > 0 ? ` y la gestión de ${totalEmpleados} ${totalEmpleados === 1 ? "empleado" : "empleados"}` : ""}.
                Precios en pesos uruguayos IVA incluido.
              </>
            )}
          </p>

          <PrecioBlock
            estilo={c.estiloPrecio}
            c={c}
            total={total}
            promoTotal={promoTotal}
            fmt={fmt}
            totalUnidades={totalUnidades}
            totalEmpleados={totalEmpleados}
          />

          <div className="quad" style={{ marginTop: 16 }}>
            <div className="card">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </div>
              <h3>Onboarding sin costo</h3>
              <p style={{ fontSize: "14pt", color: "var(--blue)", margin: "6px 0 2px" }}>Bonificado</p>
              <p>Migración de datos, alta de unidades y capacitación del equipo, sin cargo para la copropiedad.</p>
            </div>
            <div className="card dark">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 17l5-5 4 4 7-7" />
                  <path d="M14 7h7v7" />
                </svg>
              </div>
              <h3>Ajuste anual por IPC</h3>
              <p>Los honorarios se actualizan una vez al año conforme al Índice de Precios al Consumo del INE. Sin sorpresas.</p>
            </div>
          </div>

          <p className="tiny muted" style={{ marginTop: 10 }}>
            No se incluyen tasas e impuestos municipales, honorarios de terceros (escribanos, peritos, abogados), ni la contratación de proveedores cuya decisión corresponda a la Asamblea.
          </p>
        </div>
        <div className="pfooter">
          <div className="br"><img src={isotipoHabitia} alt="" />habitia · Propuesta de Administración</div>
          <div className="pn">04 / 06</div>
        </div>
      </section>

      {/* ============== PAGINA 5 — CONDICIONES + FIRMA ============== */}
      <section className="page">
        <div className="page-pad">
          <span className="sec-tag">04 · Términos y forma de pago</span>
          <h2 className="big">Condiciones comerciales.</h2>

          <div className="chips" style={{ marginTop: 18 }}>
            <div className="chip">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="4" width="18" height="16" rx="2" />
                  <path d="M8 2v4M16 2v4M3 10h18" />
                </svg>
              </div>
              <div>
                <h4>Vigencia anual</h4>
                <p>Renovación automática salvo aviso con 60 días según Ley 10.751.</p>
              </div>
            </div>
            <div className="chip">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="9" />
                  <path d="M12 7v5l3 2" />
                </svg>
              </div>
              <div>
                <h4>Validez de la oferta</h4>
                <p>{c.validez} desde la fecha de emisión.</p>
              </div>
            </div>
            <div className="chip">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="6" width="20" height="14" rx="2" />
                  <path d="M2 11h20" />
                </svg>
              </div>
              <div>
                <h4>Forma de pago</h4>
                <p>Se carga en los gastos comunes del edificio contra factura electrónica.</p>
              </div>
            </div>
            <div className="chip">
              <div className="ic">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 12l2 2 4-4 4 4 5-5 3 3" />
                  <path d="M3 21h18" />
                </svg>
              </div>
              <div>
                <h4>Ajuste anual</h4>
                <p>Honorarios actualizados una vez al año por IPC del INE.</p>
              </div>
            </div>
          </div>

          {/* Panel de plataforma — screenshots reales servidas desde public/ */}
          <div className="access-panel">
            <div>
              <div className="lbl-eyb">Plataforma habitia</div>
              <h3>Acceso 24/7<br />a la plataforma.</h3>
              <p>Toda la copropiedad con acceso desde la web y la app móvil: estado de cuenta, comprobantes, novedades, asistente IA y más.</p>
            </div>
            <div className="access-stage">
              <div className="laptop">
                <div className="scr">
                  {desktopError ? (
                    <div className="scr-fallback" aria-label="Plataforma habitia · Web" />
                  ) : (
                    <img
                      src="/plataforma.png"
                      alt="Plataforma habitia · Web"
                      onError={() => setDesktopError(true)}
                    />
                  )}
                </div>
                <div className="kb" />
                <div className="kb-notch" />
              </div>
              <div className="mob">
                {mobileError ? (
                  <div className="mob-fallback" aria-label="App habitia">App</div>
                ) : (
                  <img
                    src="/app.jpeg"
                    alt="App habitia"
                    onError={() => setMobileError(true)}
                  />
                )}
              </div>
            </div>
          </div>

          <h4 style={{ margin: "22px 0 10px", fontSize: "10pt", color: "var(--blue)", letterSpacing: ".06em", textTransform: "uppercase" }}>
            Cláusulas legales
          </h4>
          <ol style={{ margin: 0, paddingLeft: 18, fontSize: "9.5pt", color: "var(--ink-2)", lineHeight: 1.65 }}>
            <li>Servicio prestado bajo la Ley Nº 10.751 de Propiedad Horizontal de la República Oriental del Uruguay y normativa concordante.</li>
            <li>Los fondos del edificio se mantienen en cuenta separada a nombre de la copropiedad. habitia no opera con fondos comunes en cuentas propias.</li>
            <li>Confidencialidad y tratamiento de datos personales conforme Ley Nº 18.331.</li>
            <li>Cualquier divergencia se intentará resolver por vía amistosa; en su defecto, las partes se someten a los tribunales ordinarios de {c.jurisdiccion || c.ciudad || "Montevideo"}.</li>
          </ol>

          <div className="sign-block">
            <div className="sign-line">
              <b>Por la copropiedad</b>Firma y aclaración · Cédula de Identidad
            </div>
            <div className="sign-line">
              <b>Por Habitia SAS</b>Firma y aclaración
            </div>
          </div>
        </div>
        <div className="pfooter">
          <div className="br"><img src={isotipoHabitia} alt="" />habitia · Propuesta de Administración</div>
          <div className="pn">05 / 06</div>
        </div>
      </section>

      {/* ============== PAGINA 6 — CIERRE ============== */}
      <section className="page closing">
        <div className="top-iso">
          <img src={logoHabitia} alt="habitia" />
        </div>
        <h2>
          La forma moderna<br />
          de administrar<br />
          <em>edificios.</em>
        </h2>
        <p className="lead">
          Si querés ver la app en vivo, agendar una demo o consultar cualquier punto de esta propuesta, escribinos. Te respondemos hoy.
        </p>

        <div className="grid">
          <div><div className="lbl">Contacto comercial</div><div className="val">{c.contacto}</div></div>
          <div><div className="lbl">Teléfono / WhatsApp</div><div className="val">{c.telefono}</div></div>
          <div><div className="lbl">Web</div><div className="val">{c.web}</div></div>
          <div>
            <div className="lbl">Atención</div>
            <div className="val">Lunes a jueves · 9:00 a 18:00<br />Viernes · 9:00 a 14:30</div>
          </div>
        </div>
        <div className="pfooter-w">
          <div>habitia · Propuesta de Administración</div>
          <div>06 / 06</div>
        </div>
      </section>
    </div>
  );
});

export default PresupuestoTemplate;
